import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import SellerDashboardLayout from '../../components/SellerDashboardLayout/SellerDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { API_BASE_URL } from '../../config/api';
import './MySupportPage.css';

const MySupportPage = () => {
  const { user } = useAuth();
  const { socket, on, off } = useSocket();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTicket, setOpenTicket] = useState(null);
  const openTicketRef = useRef(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);

  // New ticket form
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'General' });
  const [creating, setCreating] = useState(false);
  const [quickMessage, setQuickMessage] = useState('');
  const [sendingQuick, setSendingQuick] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const userEmail = user?.email || '';
  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.companyName || 'User';

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Keep ref in sync
  useEffect(() => { openTicketRef.current = openTicket; }, [openTicket]);

  useEffect(() => {
    if (userEmail) fetchTickets();
  }, [userEmail]);

  // WebSocket listeners
  useEffect(() => {
    if (!on || !off) return;

    const handleTicketReply = ({ ticketId }) => {
      fetchTickets(true);
      if (openTicketRef.current && openTicketRef.current.id === ticketId) {
        handleOpenChat(ticketId);
      }
    };
    const handleTicketUpdated = (ticket) => {
      fetchTickets(true);
      if (openTicketRef.current && openTicketRef.current.id === ticket.id) {
        // Reload full ticket to get latest status + replies
        handleOpenChat(ticket.id);
      }
    };

    on('ticket_reply', handleTicketReply);
    on('ticket_updated', handleTicketUpdated);

    return () => {
      off('ticket_reply', handleTicketReply);
      off('ticket_updated', handleTicketUpdated);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, off]);

  // Join/leave ticket room
  useEffect(() => {
    if (!socket || !openTicket) return;
    socket.emit('join_ticket', openTicket.id);
    return () => { socket.emit('leave_ticket', openTicket.id); };
  }, [socket, openTicket?.id]);

  // Fallback polling every 5 seconds
  useEffect(() => {
    if (!userEmail) return;
    const interval = setInterval(() => fetchTickets(true), 5000);
    return () => clearInterval(interval);
  }, [userEmail]);

  // Fallback polling for open conversation every 4 seconds
  useEffect(() => {
    if (!openTicket) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/support/tickets/${openTicket.id}`, { headers: authHeaders() });
        const data = await res.json();
        if (data.success) setOpenTicket(data.ticket);
      } catch (e) { /* ignore */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [openTicket?.id]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [openTicket]);

  const fetchTickets = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await fetch(`${API_BASE_URL}/support/user-tickets/${encodeURIComponent(userEmail)}`, { headers: authHeaders() });
      if (!res.ok) {
        console.error('Fetch tickets failed:', res.status);
        if (!silent) setFetchError(`Failed to load tickets (${res.status})`);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets);
        setFetchError(null);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (!silent) setFetchError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChat = async (ticketId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets/${ticketId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setOpenTicket(data.ticket);
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length) { alert(`${oversized.map(f => `"${f.name}"`).join(', ')} exceed${oversized.length === 1 ? 's' : ''} the 10MB limit.`); }
    const valid = files.filter(f => f.size <= 10 * 1024 * 1024);
    setAttachments(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSendChat = async () => {
    if (!chatMessage.trim() && attachments.length === 0) return;
    if (!openTicket) return;
    setSendingChat(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('senderId', user?.id || '');
      formData.append('senderName', userName);
      formData.append('senderEmail', userEmail);
      formData.append('senderRole', 'user');
      formData.append('message', chatMessage.trim());
      attachments.forEach(file => formData.append('files', file));

      const res = await fetch(`${API_BASE_URL}/support/tickets/${openTicket.id}/replies`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setChatMessage('');
        setAttachments([]);
        handleOpenChat(openTicket.id);
        fetchTickets(true);
      } else {
        console.error('Send failed:', data.error);
        alert(`Failed to send: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingChat(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      alert('Please fill in subject and message');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          userId: user?.id || null,
          userName: userName,
          userEmail: userEmail,
          subject: newTicket.subject,
          message: newTicket.message,
          category: newTicket.category || 'General',
          priority: 'Medium'
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowNewTicket(false);
        setNewTicket({ subject: '', message: '', category: 'General' });
        fetchTickets(true);
      } else {
        alert(data.error || 'Failed to create ticket. Please try again.');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setCreating(false);
    }
  };

  // Quick message: auto-create ticket and send
  const handleQuickMessage = async () => {
    if (!quickMessage.trim()) return;
    setSendingQuick(true);
    try {
      const msg = quickMessage.trim();
      const subject = msg.length > 50 ? msg.substring(0, 50) + '...' : msg;
      const res = await fetch(`${API_BASE_URL}/support/tickets`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          userId: user?.id || null,
          userName: userName,
          userEmail: userEmail,
          subject: subject,
          message: msg,
          category: 'General',
          priority: 'Medium'
        })
      });
      const data = await res.json();
      if (data.success) {
        setQuickMessage('');
        await fetchTickets(true);
        // Auto-open the newly created ticket
        if (data.ticket?.id) {
          handleOpenChat(data.ticket.id);
        }
      } else {
        alert(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending quick message:', error);
    } finally {
      setSendingQuick(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA');
  };

  const formatChatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Open': return 'ms-status-open';
      case 'In Progress': return 'ms-status-progress';
      case 'Resolved': return 'ms-status-resolved';
      case 'Closed': return 'ms-status-closed';
      default: return '';
    }
  };

  if (!user) {
    return (
      <SellerDashboardLayout>
        <div className="ms-content">
          <div className="ms-login-prompt">
            <h2>Please log in to view your support tickets</h2>
            <p>You need to be logged in to access your support conversations.</p>
          </div>
        </div>
      </SellerDashboardLayout>
    );
  }

  // Helper: parse attachments from reply
  const parseAttachments = (raw) => {
    if (!raw) return [];
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return []; }
  };

  const renderAttachments = (files) => {
    if (!files || !files.length) return null;
    return (
      <div className="ms-attachments">
        {files.map((file, i) => {
          // Support both {url} (new server upload) and {data} (legacy base64)
          const src = file.url
            ? (file.url.startsWith('http') ? file.url : `${API_BASE_URL.replace('/api', '')}${file.url}`)
            : file.data;
          const isImage = file.type && file.type.startsWith('image/');
          return (
            <div key={i} className="ms-attachment-item">
              {isImage ? (
                <a href={src} download={file.name} className="ms-attachment-img-link" target="_blank" rel="noreferrer">
                  <img src={src} alt={file.name} className="ms-attachment-img" />
                  <span className="ms-attachment-img-name">{file.name}</span>
                </a>
              ) : (
                <a href={src} download={file.name} className="ms-attachment-file" target="_blank" rel="noreferrer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5"/></svg>
                  <span>{file.name}</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="1.5"/><polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="1.5"/><line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="1.5"/></svg>
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ====== CONVERSATION VIEW ======
  if (openTicket) {
    const replies = openTicket.replies || [];
    const currentUserId = user?.id;

    // isMine: returns true if this message was sent by the currently logged-in user
    const isMineTicket = openTicket.userId === currentUserId;

    const renderBubble = (isMine, avatarChar, senderLabel, isAdmin, timestamp, messageText, attachmentFiles, key) => (
      <div key={key} className={`ms-bubble ${isMine ? 'ms-bubble-mine' : 'ms-bubble-theirs'}`}>
        {!isMine && (
          <div className={`ms-bubble-avatar ${isAdmin ? 'ms-avatar-admin' : 'ms-avatar-user'}`}>{avatarChar}</div>
        )}
        <div className="ms-bubble-body">
          <div className={`ms-bubble-meta ${isMine ? 'ms-meta-right' : ''}`}>
            {!isMine && <span className="ms-bubble-name">{senderLabel}{isAdmin && <span className="ms-admin-badge">Admin</span>}</span>}
            <span className="ms-bubble-time">{timestamp}</span>
            {isMine && <span className="ms-bubble-name">You</span>}
          </div>
          {messageText && (
            <div className={`ms-bubble-text ${isMine ? 'ms-text-mine' : (isAdmin ? 'ms-text-admin' : 'ms-text-theirs')}`}>
              {messageText}
            </div>
          )}
          {renderAttachments(attachmentFiles)}
        </div>
        {isMine && (
          <div className={`ms-bubble-avatar ms-avatar-mine`}>{avatarChar}</div>
        )}
      </div>
    );

    return (
      <SellerDashboardLayout>
        <div className="ms-content">
          <div className="ms-chat-wrapper">
            <div className="ms-chat-header">
              <button className="ms-back-btn" onClick={() => { setOpenTicket(null); setChatMessage(''); setAttachments([]); }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Back
              </button>
              <div className="ms-chat-title-area">
                <span className="ms-chat-ticket-no">{openTicket.ticketNo}</span>
                <span className={`ms-status-badge ${getStatusClass(openTicket.status)}`}>{openTicket.status}</span>
              </div>
            </div>

            <div className="ms-chat-subject">
              <h3>{openTicket.subject}</h3>
              <p>Category: {openTicket.category} &bull; Created: {formatDate(openTicket.createdAt)}</p>
            </div>

            <div className="ms-chat-messages">
              {/* Original ticket message */}
              {renderBubble(
                isMineTicket,
                isMineTicket ? (openTicket.userName?.charAt(0)?.toUpperCase() || 'U') : (openTicket.userName?.charAt(0)?.toUpperCase() || 'U'),
                openTicket.userName || 'User',
                false,
                formatChatTime(openTicket.createdAt),
                openTicket.message,
                [],
                'ticket-original'
              )}

              {/* Legacy admin reply */}
              {replies.length === 0 && openTicket.adminReply && renderBubble(
                false,
                'A',
                'Support Team',
                true,
                formatChatTime(openTicket.repliedAt),
                openTicket.adminReply,
                [],
                'legacy-admin-reply'
              )}

              {/* Threaded replies */}
              {replies.map((reply) => {
                const isAdminReply = reply.senderRole === 'admin';
                const isMineReply = reply.senderId === currentUserId && !isAdminReply;
                const avatarChar = isAdminReply ? 'A' : (reply.senderName?.charAt(0)?.toUpperCase() || 'U');
                const label = isAdminReply ? 'Support Team' : (reply.senderName || 'User');
                const files = parseAttachments(reply.attachments);
                return renderBubble(isMineReply, avatarChar, label, isAdminReply, formatChatTime(reply.createdAt), reply.message, files, reply.id);
              })}

              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            {openTicket.status === 'Open' || openTicket.status === 'In Progress' ? (
              <div className="ms-chat-input-area">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.txt,.zip"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                {attachments.length > 0 && (
                  <div className="ms-attach-preview-row">
                    {attachments.map((f, i) => (
                      <div key={i} className="ms-attach-chip">
                        <span className="ms-attach-chip-name">{f.name}</span>
                        <button className="ms-attach-chip-remove" onClick={() => removeAttachment(i)} title="Remove">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="ms-input-row">
                  <button
                    className="ms-attach-btn"
                    title="Attach file"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <textarea
                    className="ms-chat-input"
                    placeholder="Type your message..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    rows="2"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                  />
                  <button
                    className="ms-send-btn"
                    onClick={handleSendChat}
                    disabled={sendingChat || (!chatMessage.trim() && attachments.length === 0)}
                  >
                    {sendingChat ? (
                      <span className="ms-sending-dots">...</span>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M18.3332 1.66669L9.1665 10.8334M18.3332 1.66669L12.4998 18.3334L9.1665 10.8334M18.3332 1.66669L1.6665 7.50002L9.1665 10.8334" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </button>
                </div>
              </div>
            ) : openTicket.status === 'Resolved' ? (
              <div className="ms-chat-resolved-notice">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                This ticket is resolved. Please create a new ticket for further queries.
                <button className="ms-new-ticket-link" onClick={() => { setOpenTicket(null); setTimeout(() => setShowNewTicket(true), 100); }}>Create New Ticket</button>
              </div>
            ) : (
              <div className="ms-chat-closed-notice">This ticket has been closed.</div>
            )}
          </div>
        </div>
      </SellerDashboardLayout>
    );
  }

  // ====== TICKET LIST VIEW ======
  return (
    <>
    <SellerDashboardLayout>
      <div className="ms-content">
        <div className="ms-header">
          <div>
            <h1 className="ms-title">My Support Tickets</h1>
            <p className="ms-subtitle">View and manage your support conversations</p>
          </div>
          <button className="ms-new-ticket-btn" onClick={() => setShowNewTicket(true)}>
            + New Ticket
          </button>
        </div>

        {/* Quick Chat with Admin */}
        <div className="ms-quick-chat">
          <div className="ms-quick-chat-label">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span>Quick Message to Admin</span>
          </div>
          <div className="ms-quick-chat-input-row">
            <input
              type="text"
              className="ms-quick-chat-input"
              placeholder="Type a message to start a conversation with admin..."
              value={quickMessage}
              onChange={(e) => setQuickMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickMessage(); } }}
              disabled={sendingQuick}
            />
            <button className="ms-quick-send-btn" onClick={handleQuickMessage} disabled={sendingQuick || !quickMessage.trim()}>
              {sendingQuick ? '...' : (
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M18.3332 1.66669L9.1665 10.8334M18.3332 1.66669L12.4998 18.3334L9.1665 10.8334M18.3332 1.66669L1.6665 7.50002L9.1665 10.8334" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="ms-loading">Loading your tickets...</div>
        ) : fetchError ? (
          <div className="ms-empty">
            <p style={{ color: '#ef4444' }}>{fetchError}</p>
            <button className="ms-new-ticket-btn" onClick={() => fetchTickets()} style={{ marginTop: 8 }}>Retry</button>
          </div>
        ) : tickets.length === 0 ? (
          <div className="ms-empty">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <path d="M32 56C45.2548 56 56 45.2548 56 32C56 18.7452 45.2548 8 32 8C18.7452 8 8 18.7452 8 32C8 45.2548 18.7452 56 32 56Z" stroke="#CBD5E1" strokeWidth="2"/>
              <path d="M24 28H40M24 36H36" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3>No support tickets yet</h3>
            <p>When you contact us, your tickets will appear here.</p>
            <button className="ms-new-ticket-btn" onClick={() => setShowNewTicket(true)}>Create a Ticket</button>
          </div>
        ) : (
          <div className="ms-ticket-list">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="ms-ticket-card" onClick={() => handleOpenChat(ticket.id)}>
                <div className="ms-ticket-top">
                  <span className="ms-ticket-no">{ticket.ticketNo}</span>
                  <span className={`ms-status-badge ${getStatusClass(ticket.status)}`}>{ticket.status}</span>
                </div>
                <h3 className="ms-ticket-subject">{ticket.subject}</h3>
                <p className="ms-ticket-preview">{ticket.message?.substring(0, 100)}{ticket.message?.length > 100 ? '...' : ''}</p>
                <div className="ms-ticket-bottom">
                  <span className="ms-ticket-cat">{ticket.category}</span>
                  <span className="ms-ticket-date">{formatDate(ticket.createdAt)}</span>
                  {ticket.replies?.length > 0 && (
                    <span className="ms-ticket-replies">{ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </SellerDashboardLayout>

      {/* New Ticket Modal - rendered via portal to escape layout stacking context */}
      {showNewTicket && ReactDOM.createPortal(
        <div className="ms-modal-overlay" onClick={() => setShowNewTicket(false)}>
          <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ms-modal-header">
              <h2>New Support Ticket</h2>
              <button className="ms-modal-close" onClick={() => setShowNewTicket(false)}>&times;</button>
            </div>
            <div className="ms-modal-body">
              <div className="ms-form-group">
                <label>Category</label>
                <select value={newTicket.category} onChange={(e) => setNewTicket({...newTicket, category: e.target.value})} className="ms-select">
                  <option value="General">General</option>
                  <option value="Billing">Billing</option>
                  <option value="Account">Account</option>
                  <option value="Technical">Technical</option>
                  <option value="Verification">Verification</option>
                </select>
              </div>
              <div className="ms-form-group">
                <label>Subject *</label>
                <input type="text" value={newTicket.subject} onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})} className="ms-input" placeholder="Brief description of your issue" />
              </div>
              <div className="ms-form-group">
                <label>Message *</label>
                <textarea value={newTicket.message} onChange={(e) => setNewTicket({...newTicket, message: e.target.value})} className="ms-textarea" rows="5" placeholder="Describe your issue in detail..." />
              </div>
              <div className="ms-modal-actions">
                <button className="ms-btn ms-btn-cancel" onClick={() => setShowNewTicket(false)}>Cancel</button>
                <button className="ms-btn ms-btn-submit" onClick={handleCreateTicket} disabled={creating}>
                  {creating ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MySupportPage;
